/* Add-to-cart confirmation view — behaviour for snippets/llm-atc-confirm.liquid.
   Approved mockup 2026-09-22: https://claude.ai/code/artifact/4437ac02-0bfa-4708-a752-b38cf618073f

   Entry point is window.LLMAtcConfirm.show(addedItem), called by cart-drawer.js after it has
   re-rendered the drawer's sections. It must run AFTER that render: renderContents() replaces the
   whole innerHTML of <cart-drawer>, so anything written before is discarded. The same trap already
   bit the old "Added to cart!" toast, which is why that one toggles a class on the persistent
   element instead of touching the banner directly.
*/
(function () {
  'use strict';

  var FREE_SHIP_THRESHOLD_CENTS = null;   // resolved lazily from the theme's own setting

  function money(cents) {
    if (window.theme && theme.moneyFormat) {
      // Reuse the shop's format so currency/locale never drifts from the rest of the page.
      try { return theme.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2)); }
      catch (e) { /* fall through */ }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* The free-shipping threshold lives in a theme setting that the storefront exposes on the
     free-shipping-notice element. Reading it from the DOM rather than hard-coding 4900 means the
     bar can never claim a threshold the cart does not actually apply. */
  function threshold() {
    if (FREE_SHIP_THRESHOLD_CENTS !== null) return FREE_SHIP_THRESHOLD_CENTS;
    /* snippets/free-shipping-notice.liquid renders data-minimum IN CENTS, already resolved for the
       visitor's market (settings.free_shipping_minimum can hold per-country values). Reading its
       output rather than recomputing keeps one source of truth — a bar promising a threshold the
       cart does not apply would be worse than no bar. Measured live: data-minimum="4900". */
    var el = document.querySelector('[data-minimum]');
    var v = el && parseInt(el.getAttribute('data-minimum'), 10);
    FREE_SHIP_THRESHOLD_CENTS = v || 0;
    return FREE_SHIP_THRESHOLD_CENTS;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function renderItem(root, item) {
    var box = root.querySelector('[data-atc-item]');
    if (!box || !item) return;
    /* "Default Title" is Shopify's placeholder on a single-variant product and must never be shown.
       It reaches customers constantly on themes that print variant_title unguarded. */
    var variant = (item.variant_title && item.variant_title !== 'Default Title')
      ? item.variant_title.replace(/ \/ /g, ' · ') : '';
    var img = item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_128x$1$2') : '';
    var unit = '';
    if (item.unit_price && item.unit_price_measurement && item.unit_price_measurement.reference_unit) {
      unit = '<span class="llm-atc__item-ea">(' + esc(money(item.unit_price)) + ' / '
           + esc(item.unit_price_measurement.reference_unit) + ')</span>';
    }
    box.innerHTML =
      (img ? '<img src="' + esc(img) + '" width="64" height="64" alt="' + esc(item.product_title || item.title) + '" aria-hidden="true" loading="lazy">' : '') +
      '<div class="llm-atc__item-meta">' +
        '<p class="llm-atc__item-ttl">' + esc(item.product_title || item.title) + '</p>' +
        (variant ? '<p class="llm-atc__item-var">' + esc(variant) + '</p>' : '') +
        '<span class="llm-atc__item-pr">' + esc(money(item.line_price != null ? item.line_price : item.price)) + '</span> ' + unit +
        '<div class="llm-atc__item-qty">Qty ' + (item.quantity || 1) + '</div>' +
        autoshipTag(root, item) +
      '</div>';
  }

  /* "Autoship · every 1 week" on the confirmation, matching the badge on the account order lines.
     WHY IT MATTERS HERE MOST: an Autoship add is a RECURRING commitment, and this panel is the last
     screen four out of five customers see before Checkout (78.6% single-product orders — see the
     note in llm-atc-confirm.liquid). If the drawer does not say the line repeats, the only
     confirmation of a subscription is the order email, after the charge.

     The interval comes from the cart line's own selling plan, so a plan renamed in Appstle's admin
     needs no change here. Shopify names plans with the bare interval ("1 week"), hence the added
     "every"; the every-prefix guard mirrors snippets/llm-account-order-line.liquid so the two
     surfaces cannot word the same fact differently. ⚠️ That label rule now exists in BOTH Liquid
     and JS because the account page is server-rendered and this panel is not — if you change the
     wording, change it in both or they will drift. */
  function autoshipTag(root, item) {
    var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    if (!plan) return '';
    var name = String(plan.name || '').trim();
    var label = /^every/i.test(name) ? name.toLowerCase() : (name ? 'every ' + name : '');
    // The mark is cloned from the Liquid-rendered <template>, never pasted in here.
    var tpl = root.querySelector('[data-atc-as-icon]');
    var icon = tpl ? tpl.innerHTML : '';
    return '<span class="llm-atc__item-as">' + icon + 'Autoship'
      + (label ? ' &middot; ' + esc(label) : '') + '</span>';
  }

  function renderShipping(root, cart) {
    var box = root.querySelector('[data-atc-ship]');
    var txt = root.querySelector('[data-atc-ship-txt]');
    var fill = root.querySelector('[data-atc-ship-fill]');
    var t = threshold();
    /* No threshold configured => no bar. Inventing one would promise free shipping the checkout
       will not honour. */
    if (!box || !t) { if (box) box.hidden = true; return; }
    box.hidden = false;
    var left = t - cart.total_price;
    if (left > 0) {
      box.classList.remove('is-free');
      txt.innerHTML = 'You&rsquo;re <b>' + esc(money(left)) + '</b> away from FREE shipping';
      fill.style.width = Math.max(2, Math.min(100, cart.total_price / t * 100)) + '%';
    } else {
      box.classList.add('is-free');
      txt.textContent = '✓ Your order ships free';
      fill.style.width = '100%';
    }
  }

  function renderTotals(root, cart) {
    var sub = root.querySelector('[data-atc-subtotal]');
    if (sub) sub.textContent = money(cart.total_price);
    var vc = root.querySelector('[data-atc-viewcart]');
    if (vc) {
      /* The count is why people open the cart. Putting it in the link answers the question without
         the navigation. */
      var n = cart.item_count || 0;
      vc.textContent = 'View cart (' + n + (n === 1 ? ' item' : ' items') + ')';
    }
  }

  /* Cross-sells come from a Liquid SECTION rendered against the product's own URL, because Liquid
     can read custom.complements and format money and /products/x.js cannot. */
  function loadCrossSells(root, handle) {
    var box = root.querySelector('[data-atc-xs]');
    if (!box) return;
    box.innerHTML = '';
    if (!handle) return;
    fetch('/products/' + encodeURIComponent(handle) + '?section_id=llm-atc-crosssell', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var block = tmp.querySelector('[data-xs-block]');
        /* Absent block = this product has no complements above threshold. Render nothing; a padded
           suggestion is what makes these modules ignorable. */
        if (block) box.appendChild(block);
      })
      .catch(function () { /* a missing cross-sell must never break the confirmation */ });
  }

  /* Inline add for single-variant complements. Multi-variant ones carry .js-quick-add instead and
     are handled by the theme's own quick-add drawer — see the note in the section. */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-xs-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var id = btn.getAttribute('data-variant-id');
    if (!id) return;
    btn.classList.add('is-busy');
    btn.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function () {
        btn.classList.remove('is-busy');
        btn.textContent = '✓ Added';
        /* ⚠️ TWO DIFFERENT SURFACES RENDER THESE ROWS, and they want opposite things on success.
           This handler is delegated on the DOCUMENT, so it fires for both.

           · Inside the add-to-cart confirmation ([data-atc-confirm]): patch the totals and the
             free-shipping bar IN PLACE. Watching the bar move IS the mechanic, and re-rendering
             would wipe this view and dump the customer back into the cart list mid-decision.
           · Inside the CART SLIDE-OUT: the opposite — the drawer IS the cart list, so leaving it
             untouched would show a cart that silently disagrees with what was just added. Ask the
             drawer to re-render itself; that also re-runs the complements block, which then
             correctly drops the product that is now in the cart. */
        var inConfirm = !!btn.closest('[data-atc-confirm]');
        return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
          var root = document.querySelector('[data-atc-confirm]');
          if (inConfirm && root) { renderShipping(root, cart); renderTotals(root, cart); }
          if (!inConfirm) {
            var drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.refresh === 'function') drawer.refresh();
          }
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble && window.theme) {
            fetch('?section_id=cart-icon-bubble').then(function (r) { return r.text(); })
              .then(function (h) {
                var t2 = document.createElement('div'); t2.innerHTML = h;
                var s = t2.querySelector('.shopify-section');
                if (s) bubble.innerHTML = s.innerHTML;
              }).catch(function () {});
          }
        });
      })
      .catch(function () {
        btn.classList.remove('is-busy');
        btn.disabled = false;
        btn.textContent = 'Add';
      });
  });

  /* ⚠️ THE ETA HAS TO BE REWRITTEN, NOT STYLED DOWN.
     llm-shipping-eta.js emits ONE flat span containing two sentences separated by a <br>:
        <strong>Ships tomorrow</strong> to <span class="shipping-city">Ambler</span>, if ordered in
        the next <span class="free-ship-time-left">16 hrs 13 mins</span><br>
        <strong>Estimated delivery</strong>: Thursday to Saturday
     Neither half has its own wrapper, so there is no selector that can hide one and keep the other
     — CSS cannot solve this. Parse the halves and keep the delivery date.

     Owner, 2026-09-22 preferred the mockup's treatment over the live one: label + a single
     "Arrives …" line. The dispatch countdown is DROPPED here on purpose.
     ⚠️ That countdown is a real urgency lever ("order in the next 16 hrs"). It still runs on the
     PDP and cart, where there is room for it; if it should come back into the drawer, this is the
     function that removed it. */
  function condenseEta(root) {
    var out = root.querySelector('.llm-atc__eta-v');
    /* ⚠️ READ FROM THE HIDDEN SOURCE, WRITE TO THE VISIBLE LINE — they must never be the same
       element. llm-shipping-eta.js rewrites every `.custom_message` on a 1s interval, so writing
       into one is a fight that produces exactly the reported symptom: the line flickering between
       the condensed and full text, then settling on the full text once the countdown ticks again.
       See the note in llm-atc-confirm.liquid. */
    var src = root.querySelector('.llm-atc__eta-src');
    if (!out || !src) return;
    var tries = 0;

    function paint() {
      var msg = src.querySelector('.custom-shipping-message');
      /* ⭐ PREFER THE REAL DATES. llm-shipping-eta.js computes dayjs objects for the delivery
         window and then formats them down to weekday names ("Thursday to Saturday") for the PDP
         copy. Weekday-only is ambiguous — "Thursday" gives no clue whether that is in two days or
         nine. It now also emits the formatted dates as data-eta-start / data-eta-end, so the
         drawer can show "Arrives Wed, Sep 30 – Fri, Oct 2" (owner, 2026-09-22).
         ⛔ Do NOT derive these from the weekday names here. That would mean re-implementing the
         cutoff and business-day logic that script already owns, and the two copies would disagree
         the first time either changed. Read its answer; never recompute it. */
      var ds = msg && msg.getAttribute('data-eta-start');
      var de = msg && msg.getAttribute('data-eta-end');
      if (ds && de) {
        out.textContent = 'Arrives ' + ds + (de !== ds ? ' – ' + de : '');
        return true;
      }
      var html = msg && msg.innerHTML;
      if (html) {
        var parts = html.split(/<br\s*\/?>/i);
        var line = parts.length > 1 ? parts[parts.length - 1] : parts[0];
        var txt = line.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        /* "Estimated delivery: Thursday to Saturday" -> "Arrives Thursday to Saturday" */
        txt = txt.replace(/^\s*estimated delivery\s*:?\s*/i, '');
        if (txt) { out.textContent = 'Arrives ' + txt; return true; }
      }
      return false;
    }

    (function poll() {
      /* The script fills asynchronously (it geolocates first), so a single read after show() is a
         race. Give it ~3s, then leave the "Calculating…" placeholder rather than blanking the row. */
      if (paint()) return;
      if (++tries < 30) setTimeout(poll, 100);
    })();

    /* Keep the visible line in step with the source. The dates only move when the order cutoff
       rolls over, but that DOES happen while a drawer sits open — and re-reading is free because
       we no longer share an element with the script, so there is no write-loop to guard against. */
    if (window.MutationObserver && !src.__llmObserved) {
      src.__llmObserved = true;
      new MutationObserver(function () { paint(); })
        .observe(src, { childList: true, subtree: true, attributes: true });
    }
  }

  /* ---- Autoship offer on the confirmation (owner 2026-10-25) ------------------------------------
     The cart list (hidden behind this view) already renders a working One-time / Autoship widget per
     line — cart-items.liquid markup, llm-autoship.js wiring (/cart/change with the selling plan).
     The added line's widget is MOVED here rather than rebuilt, so there is one implementation.
     Moved, not cloned: two copies would share radio names and data-line.

     ⚠️ A toggle re-renders the whole drawer. llm-autoship.js's applyChange() replaces <cart-drawer>'s
     innerHTML, which resets this panel to its empty, hidden Liquid shell while `llm-atc-on` (on the
     persistent element) still hides the cart list: a blank drawer. It then fires on:cart:change, so
     that is where the panel is re-shown for the same line (the index survives a selling-plan change;
     the line KEY does not, as the plan is part of it), and the line's new price with it. Other cart edits go through cart-drawer.js,
     which calls hide() first, so `llm-atc-on` is gone and this listener stands down. */
  var atcLine = null;
  var moved = null;   // { widget, home, next } — where the widget came from, to put it back on hide()

  function restoreAutoship() {
    if (moved && moved.home && moved.home.isConnected) {
      moved.home.insertBefore(moved.widget, moved.next && moved.next.parentNode === moved.home ? moved.next : null);
    }
    moved = null;
  }

  function mountAutoship(drawer, root, line) {
    var slot = root.querySelector('[data-atc-as]');
    if (!slot) return;
    restoreAutoship();
    slot.innerHTML = '';
    if (!line) return;
    var widget = drawer.querySelector('.drawer__content .llm-autoship[data-line="' + line + '"]');
    if (!widget) return;            // no Autoship plan on this product
    moved = { widget: widget, home: widget.parentNode, next: widget.nextSibling };
    slot.appendChild(widget);
    // The widget states the plan itself, so the "Autoship · every N" pill would say it twice.
    var tag = root.querySelector('.llm-atc__item-as');
    if (tag) tag.remove();
  }

  document.addEventListener('on:cart:change', function () {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.classList.contains('llm-atc-on') || !atcLine) return;
    // Re-show even if the panel survived the re-render: its price and the moved widget are then
    // stale (the cart list got fresh ones), and show() swaps both in.
    var line = atcLine;
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      var item = cart.items[line - 1];
      if (!item) { window.LLMAtcConfirm.hide(); return; }
      window.LLMAtcConfirm.show(item);
    }).catch(function () { window.LLMAtcConfirm.hide(); });
  });

  window.LLMAtcConfirm = {
    /** @param {Object} added - the line item returned by /cart/add.js */
    show: function (added) {
      var drawer = document.querySelector('cart-drawer');
      var root = drawer && drawer.querySelector('[data-atc-confirm]');
      if (!drawer || !root) return false;

      root.hidden = false;
      drawer.classList.add('llm-atc-on');
      renderItem(root, added);
      loadCrossSells(root, added && added.handle);

      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        renderShipping(root, cart);
        renderTotals(root, cart);
        // Find the added line's position: the cart widgets are keyed by 1-based line index.
        var idx = -1;
        for (var i = 0; i < cart.items.length; i++) {
          if (added && cart.items[i].key === added.key) { idx = i; break; }
        }
        if (idx < 0 && added) {
          for (var j = 0; j < cart.items.length; j++) {
            if (cart.items[j].variant_id === added.variant_id) { idx = j; break; }
          }
        }
        atcLine = idx >= 0 ? idx + 1 : null;
        mountAutoship(drawer, root, atcLine);
      }).catch(function () {});

      /* The shipping ETA is filled by the global llm-shipping-eta.js, which scans for
         .custom_message. It has already run for this page, so a freshly injected mount needs a
         nudge; the script listens for this event (and re-scan is idempotent). */
      document.dispatchEvent(new CustomEvent('llm:shipping-eta:refresh'));
      condenseEta(root);
      return true;
    },

    /** Returning to the normal cart list — used when the drawer is reopened from the header. */
    hide: function () {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      restoreAutoship();
      drawer.classList.remove('llm-atc-on');
      var root = drawer.querySelector('[data-atc-confirm]');
      if (root) root.hidden = true;
    }
  };

  /* ⚠️ RESET THE VIEW WHEN THE DRAWER CLOSES.
     `llm-atc-on` lives on the PERSISTENT <cart-drawer> element — deliberately, so a re-render
     cannot wipe the confirmation mid-display. The cost of that is it also survives the drawer being
     dismissed, so the next open showed the confirmation again: add something, close, click the cart
     icon, and you get "Added to cart" instead of your cart (owner, 2026-09-23).

     cart-drawer.js only calls hide() from its own cart-change path, which a plain close/reopen never
     enters — hide()'s docstring already claimed this was handled when nothing called it. Watching
     the `open` attribute is what makes that true, and it covers EVERY way the drawer can be
     dismissed (X, Esc, overlay click, history back) without patching each one. */
  var drawerEl = document.querySelector('cart-drawer');
  if (drawerEl && window.MutationObserver) {
    new MutationObserver(function () {
      // Closed = the `open` attribute is gone. Reset only then, never on open: `show()` sets the
      // class a tick BEFORE cart-drawer.js opens the drawer, so reacting to open would undo it.
      if (drawerEl.getAttribute('open') === null) window.LLMAtcConfirm.hide();
    }).observe(drawerEl, { attributes: true, attributeFilter: ['open'] });
  }
})();
